// Splat-array helpers for the hair path: merge the shade's colours into the
// mesh's own colour words, and read per-Gaussian positions / env bounds back out.
//
// THE COLOUR WORD IS THE SAME ON BOTH SIDES OF THE SPARK REMOVAL, which is why
// `mergePackedRgb` survived it unchanged. three stores a splat's colour as four
// `Uint8ClampedArray` bytes; viewed as a `Uint32Array` that is R in bits 0..7,
// G in 8..15, B in 16..23, alpha in 24..31 — byte for byte what Spark packed
// into word 0, and what the WGSL shade's `pack4x8unorm` already emits.
//
// The half-float decode is GONE with the packed layout: three keeps centres as
// plain f32, so positions are a copy rather than an unpack.

import * as THREE from 'three';

// Write one splat's RGB into the colour word, keeping its alpha byte. Float
// [0,1] -> uint8. Used by the CPU shade path (shade.js); the GPU path writes
// whole words through mergePackedRgb below.
export function setPackedSplatRgb(pa, index, r, g, b) {
  const uR = r <= 0 ? 0 : r >= 1 ? 255 : Math.round(r * 255);
  const uG = g <= 0 ? 0 : g >= 1 ? 255 : Math.round(g * 255);
  const uB = b <= 0 ? 0 : b >= 1 ? 255 : Math.round(b * 255);
  pa[index] = (uR | (uG << 8) | (uB << 16) | (pa[index] & 0xFF000000)) >>> 0;
}

// Merge P rgba8unorm words (the WGSL shade's pack4x8unorm output) into the
// mesh's colour words: RGB comes from src[i], the alpha byte (splat opacity)
// keeps the existing value. `pa` is a Uint32Array VIEW over three's colour
// bytes — see the header for why the two layouts coincide.
export function mergePackedRgb(pa, src, P) {
  // ONE word per splat, not one in four. Spark's packed record was four words
  // and the colour sat in word 0, so this used to stride by 4; three's colour
  // attribute is four BYTES per splat, i.e. one word each. Getting that wrong is
  // silent in the worst way — a quarter of the splats take the shade and the
  // rest keep the raw PLY colour.
  for (let i = 0; i < P; i++) {
    pa[i] = ((src[i] & 0x00FFFFFF) | (pa[i] & 0xFF000000)) >>> 0;
  }
}

// Per-Gaussian positions as a flat Float32Array (P*3) plus the centroid, from a
// three splat geometry's own `position` attribute. A copy, not an unpack: the
// caller keeps it for the whole session while the geometry's array may be
// re-uploaded, and `HairData` owns its buffers.
export function extractPositionsFromGeometry(geometry, P) {
  const attr = geometry?.getAttribute?.('position');
  if (!attr) throw new Error('[hair] splat geometry has no position attribute');
  if (attr.count < P) {
    throw new Error(`[hair] splat geometry has ${attr.count} splats, manifest says ${P}`);
  }
  const src = attr.array;
  const positions = new Float32Array(src.buffer, src.byteOffset, P * 3).slice();
  let cx = 0, cy = 0, cz = 0;
  for (let i = 0; i < P; i++) {
    cx += positions[i * 3]; cy += positions[i * 3 + 1]; cz += positions[i * 3 + 2];
  }
  return { positions, centroid: new THREE.Vector3(cx / P, cy / P, cz / P) };
}

// Centroid + AABB + a generous "far" hint from an environment splat. The loft
// env was captured from a viewpoint roughly inside the room, so its centroid is
// the natural probe origin — that is where all 6 cube faces see the room walls.
// Returns null when there is no environment splat to measure.
export function computeEnvCentroidBounds(envMesh) {
  const attr = envMesh?.geometry?.getAttribute?.('position');
  if (!attr) return null;
  const E = attr.count;
  if (E === 0) return null;
  const p = attr.array;
  let ecx = 0, ecy = 0, ecz = 0;
  let xMin = Infinity, yMin = Infinity, zMin = Infinity;
  let xMax = -Infinity, yMax = -Infinity, zMax = -Infinity;
  for (let i = 0; i < E; i++) {
    const x = p[i * 3], y = p[i * 3 + 1], z = p[i * 3 + 2];
    ecx += x; ecy += y; ecz += z;
    if (x < xMin) xMin = x; if (x > xMax) xMax = x;
    if (y < yMin) yMin = y; if (y > yMax) yMax = y;
    if (z < zMin) zMin = z; if (z > zMax) zMax = z;
  }
  const envCentroid = new THREE.Vector3(ecx / E, ecy / E, ecz / E);
  const envMin = new THREE.Vector3(xMin, yMin, zMin);
  const envMax = new THREE.Vector3(xMax, yMax, zMax);
  // Generous far: 4x the longest bounding-box dimension, so splats at the far
  // edge of the room never clip even if the probe sits near one wall.
  const ext = Math.max(xMax - xMin, yMax - yMin, zMax - zMin);
  const envFarHint = Math.max(1000, ext * 4);
  return { envCentroid, envMin, envMax, envFarHint };
}
